import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  FormProvider,
  type FieldPath,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type { TGuaranteesBeneficiary } from '~/store/guarantees/types.ts';
import {
  BENEFICIARY_NAME_GUARANTEE_LANG_MAX_LENGTH,
  BENEFICIARY_REQUIRED_FIELDS,
} from '../../../rootSection/constants.ts';
import {
  buildGuaranteesBeneficiaryPayload,
  getGuaranteesBeneficiaryInitialValues,
  guaranteesBeneficiarySectionResolver,
} from '../../../utils/sections/utils.ts';
import {
  GuaranteesBeneficiaryFieldName,
  type IBeneficiarySectionProps,
} from '../../../rootSection/types.ts';
import FlowSection from '../shared/FlowSection.tsx';
import { utils } from '../shared/utils.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';
import { BeneficiaryResidenceCountryField } from './BeneficiaryResidenceCountryField.tsx';
import { useBeneficiarySectionFields } from './useBeneficiarySectionFields.ts';

const BeneficiarySection: React.FC<IBeneficiarySectionProps> = ({
  beneficiary,
  isLoading,
  isSaving,
  onSubmit,
}) => {
  const {
    isReadOnly,
    requireChangesToEnableActions,
    registerDependentSectionValidation,
  } = useGuaranteesRootApplicationSectionContext();
  const defaultValues = React.useMemo(
    () => getGuaranteesBeneficiaryInitialValues(beneficiary),
    [beneficiary]
  );
  const formMethods = useForm<TGuaranteesBeneficiary>({
    defaultValues,
    resolver: guaranteesBeneficiarySectionResolver,
    mode: 'onSubmit',
  });
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = formMethods;
  const isDisabled = isSaving || isReadOnly;
  const {
    countryInputValue,
    handleNonResidentChange,
    isNonResidentBeneficiary,
    isResidenceCountryLoading,
    residenceCountryOptions,
    setCountryInputValue,
  } = useBeneficiarySectionFields({
    beneficiary,
    control,
    defaultValues,
    getValues,
    setValue,
  });
  const { isActionDisabled } = utils({
    defaultValues,
    isDirty,
    isDisabled,
    requireChangesToEnableActions,
    reset,
  });

  const handleBeneficiarySubmit: SubmitHandler<TGuaranteesBeneficiary> = async (values) => {
    const isSaved = await onSubmit(buildGuaranteesBeneficiaryPayload(values));

    if (isSaved) {
      reset(values);
    }
  };

  const touchAndValidateFields = React.useCallback(
    (fieldNames: readonly (keyof TGuaranteesBeneficiary)[]) => {
      fieldNames.forEach((fieldName) => {
        const fieldPath = fieldName as FieldPath<TGuaranteesBeneficiary>;

        setValue(fieldPath, getValues(fieldPath), {
          shouldDirty: false,
          shouldTouch: true,
          shouldValidate: false,
        });
      });

      void trigger(fieldNames as FieldPath<TGuaranteesBeneficiary>[]);
    },
    [getValues, setValue, trigger]
  );

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      touchAndValidateFields(BENEFICIARY_REQUIRED_FIELDS);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [defaultValues, isNonResidentBeneficiary, touchAndValidateFields]);

  React.useEffect(
    () => registerDependentSectionValidation('beneficiary', () => trigger(undefined, { shouldFocus: true })),
    [registerDependentSectionValidation, trigger]
  );

  return (
    <FlowSection
      step={guaranteesClientText.beneficiarySectionStepLabel}
      title={guaranteesClientText.beneficiarySectionTitle}
      description={guaranteesClientText.beneficiarySectionDescription}
    >
      {isLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {guaranteesClientText.beneficiaryLoadingText}
          </Typography>
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(handleBeneficiarySubmit)(event);
            }}
          >
            <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
              <FormInput
                name={GuaranteesBeneficiaryFieldName.BeneficiaryName}
                label={guaranteesClientText.beneficiaryNameLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <Box sx={guaranteesClientPageStyles.flowFullWidthField}>
                <FormCheckbox
                  name={GuaranteesBeneficiaryFieldName.IsNonResidentBeneficiary}
                  label={guaranteesClientText.beneficiaryNonResidentLabel}
                  disabled={isDisabled}
                  onChange={handleNonResidentChange}
                />
              </Box>
              {!isNonResidentBeneficiary && (
                <FormInput
                  name={GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode}
                  label={guaranteesClientText.beneficiaryIdentifyCodeLabel}
                  disabled={isDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
              <FormInput
                name={GuaranteesBeneficiaryFieldName.BeneficiaryLocation}
                label={guaranteesClientText.beneficiaryLocationLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <BeneficiaryResidenceCountryField
                control={control}
                options={residenceCountryOptions}
                inputValue={countryInputValue}
                setInputValue={setCountryInputValue}
                disabled={isDisabled}
                loading={isResidenceCountryLoading}
              />
              <FormInput
                name={GuaranteesBeneficiaryFieldName.BeneficiaryNameGuaranteeLang}
                label={guaranteesClientText.beneficiaryNameGuaranteeLangLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
                inputProps={{ maxLength: BENEFICIARY_NAME_GUARANTEE_LANG_MAX_LENGTH }}
              />
              <FormInput
                name={GuaranteesBeneficiaryFieldName.BeneficiaryGuarantorBank}
                label={guaranteesClientText.beneficiaryGuarantorBankLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesBeneficiaryFieldName.BeneficiaryBank}
                label={guaranteesClientText.beneficiaryBankLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesBeneficiaryFieldName.AdvisingBank}
                label={guaranteesClientText.advisingBankLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
            </Box>

            <Box sx={guaranteesClientPageStyles.flowActions}>
              <Button
                type="submit"
                variant="contained"
                disabled={isActionDisabled}
                startIcon={isSaving ? <ButtonLoadingIcon /> : undefined}
                sx={dataFormStyles.documentPrimaryAction}
              >
                {guaranteesClientText.saveBeneficiaryButton}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      )}
    </FlowSection>
  );
};

export default BeneficiarySection;
